import { normalizeSearch } from "../AdminWorkspace";
import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  FormControl,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  Switch,
  InputAdornment,
  Tooltip,
  Link,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import {
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  MailOutline as MailOutlineIcon,
  FileCopyOutlined as FileCopyOutlinedIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Block as BlockIcon,
  Update as UpdateIcon,
} from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { head, isArray } from "lodash";
import { useDate } from "../../hooks/useDate";

import moment from "moment";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { StatusBadge, EmptyState, StatCard, Section } from "../ui";
import {
  usePanelStyles,
  ModalTitle,
  FormField,
  FormSection,
  SwitchRow,
} from "../QueueIntegrationModal/panelKit";
import { getTokens } from "../../styles/tokens";
import {
  isSuperUser,
  getBaseDomain,
  isValidSlug,
  isValidCustomDomain,
  getPanelUrl,
  TENANT_TYPES,
} from "../../utils/tenant";

// Campos de tenant (Revenda) — valores iniciais compartilhados por form e manager
const TENANT_DEFAULTS = {
  type: TENANT_TYPES.company,
  slug: "",
  customDomain: "",
  allowCustomBrand: false,
  maxCompanies: "",
};

const TENANT_TYPE_BADGE = {
  [TENANT_TYPES.platform]: { tone: "brand", key: "typePlatform" },
  [TENANT_TYPES.reseller]: { tone: "info", key: "typeReseller" },
  [TENANT_TYPES.company]: { tone: "neutral", key: "typeCompany" },
};

export const TenantTypeBadge = ({ type, size = "sm" }) => {
  const cfg = TENANT_TYPE_BADGE[type] || TENANT_TYPE_BADGE[TENANT_TYPES.company];
  return (
    <StatusBadge
      color={cfg.tone}
      size={size}
      label={i18n.t(`compaies.table.${cfg.key}`)}
    />
  );
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      width: "100%",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    metrics: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 12,
    },
    toolbar: { marginBottom: 0 },
    statusFilter: { minWidth: 140 },
    fullWidth: {
      width: "100%",
    },
    // formulário em página (não em modal): colunas conforme a largura disponível
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
      columnGap: 16,
      rowGap: 14,
      alignItems: "start",
    },
    formActions: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 20,
      paddingTop: 16,
      borderTop: `1px solid ${t.line}`,
    },
    formActionsRight: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
    },
    panelLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      color: t.brand,
      fontWeight: 600,
      fontSize: 13,
    },
    // colunas nunca quebram; a tabela rola na horizontal dentro do cartão
    table: {
      "& .MuiTableCell-root": { whiteSpace: "nowrap" },
      // a célula presa tem fundo próprio: acompanha o realce da linha
      "& tbody .MuiTableRow-root:hover $actionsCell": {
        backgroundImage: `linear-gradient(${t.rowHover}, ${t.rowHover})`,
      },
    },
    colName: { minWidth: 200 },
    colDomain: { minWidth: 180 },
    // colunas de menor valor somem abaixo de lg (1280px); as principais ficam
    hideMd: {
      [theme.breakpoints.down("md")]: {
        display: "none",
      },
    },
    sub: { display: "block", fontSize: 12, color: t.textSecondary, fontWeight: 400 },
    // vencimento próximo / vencida: tinta suave na linha + data no tom do aviso
    rowWarn: { backgroundColor: t.status.warn.bg },
    rowDown: { backgroundColor: t.status.down.bg },
    dueWarn: { color: t.status.warn.fg, fontWeight: 600 },
    dueDown: { color: t.status.down.fg, fontWeight: 600 },
    // ações presas à direita: a tabela é larga e rola na horizontal
    actionsHead: {
      position: "sticky",
      right: 0,
      zIndex: 3,
      boxShadow: `-1px 0 0 ${t.line}`,
    },
    actionsCell: {
      position: "sticky",
      right: 0,
      zIndex: 1,
      width: "1%",
      backgroundColor: t.paper,
      boxShadow: `-1px 0 0 ${t.line}`,
    },
    // a tinta da linha é translúcida no escuro: repete por cima do fundo sólido da célula presa
    actionsCellWarn: {
      backgroundImage: `linear-gradient(${t.status.warn.bg}, ${t.status.warn.bg})`,
    },
    actionsCellDown: {
      backgroundImage: `linear-gradient(${t.status.down.bg}, ${t.status.down.bg})`,
    },
    aiCell: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "flex-end",
      whiteSpace: "nowrap",
      gap: 2,
    },
    emptyCell: {
      padding: 0,
      borderBottom: "none",
    },
    domainLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      color: t.brand,
      fontWeight: 500,
    },
    inviteLinkBox: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 8px 8px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
      marginTop: 12,
    },
    inviteLinkText: {
      flex: 1,
      minWidth: 0,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 12.5,
      color: t.text,
      wordBreak: "break-all",
      userSelect: "all",
    },
  };
});

// Copia com navigator.clipboard e cai para execCommand em contextos sem permissão/HTTP.
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    /* tenta o fallback */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const panel = usePanelStyles();
  const { user } = useContext(AuthContext);
  const isSuper = isSuperUser(user);
  const baseDomain = getBaseDomain();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    generateInvoice: true,
    ...TENANT_DEFAULTS,
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format(
          "YYYY-MM-DD"
        );
      }
      return {
        ...prev,
        ...initialValue,
      };
    });
  }, [initialValue]);

  // Normaliza/valida os campos de tenant; devolve null se inválido (já mostra o toast)
  const normalizeTenantFields = (data) => {
    const out = { ...data };
    const slug = (out.slug || "").trim().toLowerCase();
    const customDomain = (out.customDomain || "")
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/[/:].*$/, "");

    if (slug && !isValidSlug(slug)) {
      toast.error(i18n.t("compaies.table.invalidSlug"));
      return null;
    }
    if (customDomain && !isValidCustomDomain(customDomain)) {
      toast.error(i18n.t("compaies.table.invalidCustomDomain"));
      return null;
    }
    out.slug = slug || null;
    out.customDomain = customDomain || null;
    out.allowCustomBrand = Boolean(out.allowCustomBrand);

    if (isSuper) {
      out.type = out.type || TENANT_TYPES.company;
      if (out.type === TENANT_TYPES.reseller && !out.slug) {
        toast.error(i18n.t("compaies.table.slugRequired"));
        return null;
      }
      const max = String(out.maxCompanies === undefined || out.maxCompanies === null ? "" : out.maxCompanies).trim();
      out.maxCompanies = max === "" ? null : Number(max);
      if (out.maxCompanies !== null && (!Number.isInteger(out.maxCompanies) || out.maxCompanies < 0)) {
        toast.error(i18n.t("compaies.table.invalidMaxCompanies"));
        return null;
      }
    } else {
      // reseller: o backend força type/parentCompanyId; não enviamos o que não pode editar
      delete out.type;
      delete out.maxCompanies;
      delete out.parentCompanyId;
    }
    return out;
  };

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    const normalized = normalizeTenantFields(data);
    if (!normalized) return;
    console.log("Dados do formulário:", normalized);
    onSubmit(normalized);
    setRecord({ ...initialValue, ...TENANT_DEFAULTS, dueDate: "", generateInvoice: true });
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate)
            .add(1, "month")
            .format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(2, "month")
            .format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(3, "month")
            .format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(6, "month")
            .format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate)
            .add(12, "month")
            .format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {(formik) => (
          <Form className={classes.fullWidth}>
            <Section
              title={record.id !== undefined ? "Editar empresa" : "Nova empresa"}
              actions={
                record.id !== undefined && getPanelUrl(record) ? (
                  <Link
                    href={getPanelUrl(record)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.panelLink}
                    underline="none"
                  >
                    <OpenInNewIcon fontSize="small" />
                    {i18n.t("compaies.table.openPanel")}
                  </Link>
                ) : null
              }
            >
              <FormSection title="Identificação">
                <div className={classes.formGrid}>
                  <FormField label={i18n.t("compaies.table.name")} htmlFor="company-name">
                    <Field
                      as={TextField}
                      id="company-name"
                      name="name"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </FormField>
                  <FormField label={i18n.t("compaies.table.email")} htmlFor="company-email" required>
                    <Field
                      as={TextField}
                      id="company-email"
                      name="email"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                    />
                  </FormField>
                  <FormField label={i18n.t("compaies.table.password")} htmlFor="company-password">
                    <Field
                      as={TextField}
                      id="company-password"
                      name="password"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </FormField>
                  <FormField label={i18n.t("compaies.table.phone")} htmlFor="company-phone">
                    <Field
                      as={TextField}
                      id="company-phone"
                      name="phone"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </FormField>
                  <FormField label={i18n.t("compaies.table.document")} htmlFor="company-document">
                    <Field
                      as={TextField}
                      id="company-document"
                      name="document"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </FormField>
                </div>
              </FormSection>

              <FormSection title="Plano e cobrança">
                <div className={classes.formGrid}>
                  <FormField label={i18n.t("compaies.table.plan")} required>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        id="plan-selection"
                        name="planId"
                        required
                        inputProps={{ "aria-label": i18n.t("compaies.table.plan") }}
                      >
                        {plans.map((plan, key) => (
                          <MenuItem key={key} value={plan.id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </FormField>
                  <FormField
                    label={i18n.t("compaies.table.dueDate")}
                    htmlFor="company-dueDate"
                    help={
                      !formik.values.dueDate
                        ? i18n.t("compaies.table.dueDateHelper")
                        : undefined
                    }
                  >
                    <Field
                      as={TextField}
                      id="company-dueDate"
                      type="date"
                      name="dueDate"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </FormField>
                  <FormField label={i18n.t("compaies.table.recurrence")}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        id="recurrence"
                        name="recurrence"
                        inputProps={{ "aria-label": i18n.t("compaies.table.recurrence") }}
                      >
                        <MenuItem value="MENSAL">{i18n.t("compaies.table.monthly")}</MenuItem>
                        <MenuItem value="BIMESTRAL">{i18n.t("compaies.table.bimonthly")}</MenuItem>
                        <MenuItem value="TRIMESTRAL">{i18n.t("compaies.table.quarterly")}</MenuItem>
                        <MenuItem value="SEMESTRAL">{i18n.t("compaies.table.semester")}</MenuItem>
                        <MenuItem value="ANUAL">{i18n.t("compaies.table.yearly")}</MenuItem>
                      </Field>
                    </FormControl>
                  </FormField>
                  <FormField label={i18n.t("compaies.table.generateInvoice")}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        id="generate-invoice-selection"
                        name="generateInvoice"
                        inputProps={{ "aria-label": i18n.t("compaies.table.generateInvoice") }}
                      >
                        <MenuItem value={true}>Sim</MenuItem>
                        <MenuItem value={false}>Não</MenuItem>
                      </Field>
                    </FormControl>
                  </FormField>
                  <FormField label={i18n.t("compaies.table.active")}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        id="status-selection"
                        name="status"
                        inputProps={{ "aria-label": i18n.t("compaies.table.active") }}
                      >
                        <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </FormField>
                </div>
              </FormSection>

              {/* Revenda: tipo / subdomínio / domínio / limite / marca. Os campos só de
                  super simplesmente não são renderizados, sem deslocar os demais. */}
              <FormSection title="Domínio e marca">
                <div className={classes.formGrid}>
                  {isSuper && (
                    <FormField label={i18n.t("compaies.table.type")}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Field
                          as={Select}
                          id="type-selection"
                          name="type"
                          disabled={record.type === TENANT_TYPES.platform}
                          inputProps={{ "aria-label": i18n.t("compaies.table.type") }}
                        >
                          {record.type === TENANT_TYPES.platform && (
                            <MenuItem value={TENANT_TYPES.platform}>
                              {i18n.t("compaies.table.typePlatform")}
                            </MenuItem>
                          )}
                          <MenuItem value={TENANT_TYPES.company}>
                            {i18n.t("compaies.table.typeCompany")}
                          </MenuItem>
                          <MenuItem value={TENANT_TYPES.reseller}>
                            {i18n.t("compaies.table.typeReseller")}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </FormField>
                  )}
                  <FormField
                    label={i18n.t("compaies.table.slug")}
                    htmlFor="company-slug"
                    help={i18n.t("compaies.table.slugHelper")}
                  >
                    <Field
                      as={TextField}
                      id="company-slug"
                      name="slug"
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="minha-revenda"
                      InputProps={{
                        endAdornment: baseDomain ? (
                          <InputAdornment position="end">.{baseDomain}</InputAdornment>
                        ) : null,
                      }}
                    />
                  </FormField>
                  <FormField
                    label={i18n.t("compaies.table.customDomain")}
                    htmlFor="company-customDomain"
                    help={i18n.t("compaies.table.customDomainHelper")}
                  >
                    <Field
                      as={TextField}
                      id="company-customDomain"
                      name="customDomain"
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="painel.suaempresa.com.br"
                    />
                  </FormField>
                  {isSuper && formik.values.type === TENANT_TYPES.reseller && (
                    <FormField
                      label={i18n.t("compaies.table.maxCompanies")}
                      htmlFor="company-maxCompanies"
                      help={i18n.t("compaies.table.maxCompaniesHelper")}
                    >
                      <Field
                        as={TextField}
                        id="company-maxCompanies"
                        name="maxCompanies"
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </FormField>
                  )}
                  <FormField label={i18n.t("compaies.table.allowCustomBrand")}>
                    <SwitchRow label={formik.values.allowCustomBrand ? "Liberada" : "Não liberada"}>
                      <Field
                        as={Switch}
                        type="checkbox"
                        name="allowCustomBrand"
                        color="primary"
                        size="small"
                        inputProps={{ "aria-label": i18n.t("compaies.table.allowCustomBrand") }}
                      />
                    </SwitchRow>
                  </FormField>
                </div>
              </FormSection>

              <div className={classes.formActions}>
                {record.id !== undefined ? (
                  <ButtonWithSpinner
                    loading={loading}
                    onClick={() => onDelete(record)}
                    disabled={record.id === 1 || record.type === TENANT_TYPES.platform}
                    className={`${panel.busyWrap} ${panel.danger}`}
                  >
                    {i18n.t("compaies.table.delete")}
                  </ButtonWithSpinner>
                ) : null}
                <div className={classes.formActionsRight}>
                  {record.id !== undefined ? (
                    <Tooltip title="Avança o vencimento em um período da recorrência">
                      <span>
                        <ButtonWithSpinner
                          loading={loading}
                          onClick={() => incrementDueDate()}
                          variant="outlined"
                          startIcon={<UpdateIcon />}
                        >
                          {i18n.t("compaies.table.dueDate")}
                        </ButtonWithSpinner>
                      </span>
                    </Tooltip>
                  ) : null}
                  <ButtonWithSpinner loading={loading} onClick={() => onCancel()}>
                    {i18n.t("compaies.table.clear")}
                  </ButtonWithSpinner>
                  <ButtonWithSpinner
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {i18n.t("compaies.table.save")}
                  </ButtonWithSpinner>
                </div>
              </div>
            </Section>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid(props) {
  const { records, onSelect, loading = false } = props;
  const classes = useStyles();
  const panel = usePanelStyles();
  const { dateToClient, datetimeToClient } = useDate();

  // Uso de IA no mês (medição por empresa) — só super
  const { user: gridUser } = useContext(AuthContext);
  const showAi = isSuperUser(gridUser);
  const [aiUsage, setAiUsage] = useState({}); // companyId → linha de GET /ai-usage/companies
  const [aiBusyId, setAiBusyId] = useState(null);

  const loadAiUsage = async () => {
    try {
      const { data } = await api.get("/ai-usage/companies");
      const map = {};
      (Array.isArray(data) ? data : []).forEach((r) => {
        map[r.companyId] = r;
      });
      setAiUsage(map);
    } catch (e) {
      // 404 = backend sem o módulo ainda; coluna fica com "-"
      if (e?.response?.status !== 404) toastError(e);
    }
  };

  useEffect(() => {
    if (showAi) loadAiUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAi]);

  const brl = (v) =>
    Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleAiExtraCredit = async (row) => {
    const raw = window.prompt(i18n.t("aiUsage.platform.extraCreditPrompt"), "0");
    if (raw === null) return;
    const amount = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(amount) || amount < 0) return;
    setAiBusyId(row.id);
    try {
      await api.post(`/ai-usage/companies/${row.id}/extra-credit`, { amount });
      toast.success(i18n.t("aiUsage.platform.extraCreditSaved"));
      await loadAiUsage();
    } catch (e) {
      toastError(e);
    } finally {
      setAiBusyId(null);
    }
  };

  const handleAiEnforce = async (row, enforce) => {
    setAiBusyId(row.id);
    try {
      const { data } = await api.put(`/ai-usage/companies/${row.id}/enforce`, { enforce });
      setAiUsage((m) => ({
        ...m,
        [row.id]: { ...(m[row.id] || { companyId: row.id }), enforce: !!data?.aiEnforce },
      }));
      toast.success(i18n.t("aiUsage.platform.enforceSaved"));
    } catch (e) {
      toastError(e);
    } finally {
      setAiBusyId(null);
    }
  };

  const renderAiUsage = (row) => {
    const u = aiUsage[row.id];
    const busy = aiBusyId === row.id;
    return (
      <span className={classes.aiCell}>
        <span>{u ? `${brl(u.usedBrl)} / ${brl(u.creditBrl)}` : "-"}</span>
        <Tooltip title={i18n.t("aiUsage.platform.extraCredit")}>
          <span>
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => handleAiExtraCredit(row)}
              aria-label={i18n.t("aiUsage.platform.extraCredit")}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={i18n.t("aiUsage.platform.enforce")}>
          <Switch
            size="small"
            color="primary"
            checked={!!(u && u.enforce)}
            disabled={busy}
            onChange={(e) => handleAiEnforce(row, e.target.checked)}
            inputProps={{ "aria-label": i18n.t("aiUsage.platform.enforce") }}
          />
        </Tooltip>
      </span>
    );
  };

  // Convite de onboarding (Revenda — Fase C)
  const [invite, setInvite] = useState(null); // { company, inviteUrl, expiresAt }
  const [invitingId, setInvitingId] = useState(null);

  const handleInvite = async (row) => {
    if (invitingId) return;
    setInvitingId(row.id);
    try {
      const { data } = await api.post(`/companies/${row.id}/invite`);
      setInvite({ company: row, inviteUrl: data.inviteUrl, expiresAt: data.expiresAt });
      toast.success(i18n.t("compaies.table.inviteSent"));
    } catch (e) {
      toast.error(i18n.t("compaies.table.inviteError"));
    } finally {
      setInvitingId(null);
    }
  };

  const handleCopyInvite = async () => {
    if (!invite) return;
    const ok = await copyToClipboard(invite.inviteUrl);
    if (ok) toast.success(i18n.t("compaies.table.inviteCopied"));
    else toast.error(i18n.t("compaies.table.inviteCopyError"));
  };

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderPlanValue = (row) => {
    return row.planId !== null ? row.plan && row.plan.amount ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00' : "-";
  };

  const renderDomain = (row) => {
    const url = getPanelUrl(row);
    if (!url) return "-";
    const host = url.replace(/^https?:\/\//, "");
    return (
      <Tooltip title={i18n.t("compaies.table.openPanel")}>
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.domainLink}
          underline="none"
          onClick={(e) => e.stopPropagation()}
        >
          {host}
          <OpenInNewIcon style={{ fontSize: 14 }} />
        </Link>
      </Tooltip>
    );
  };

  // "warn" = vence em até 5 dias; "down" = vencida; null = em dia
  const dueTone = (record) => {
    if (moment(record.dueDate).isValid()) {
      const diff = moment(record.dueDate).diff(moment(), "days");
      if (diff >= 1 && diff <= 5) return "warn";
      if (diff <= 0) return "down";
    }
    return null;
  };

  const colCount = showAi ? 13 : 12;

  return (
    <Paper className={`${panel.tableCard} ${panel.tableCardAuto}`} elevation={0}>
      <Table className={classes.table} size="small" aria-label={i18n.t("compaies.title")}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.colName}>{i18n.t("compaies.table.name")}</TableCell>
            <TableCell>{i18n.t("compaies.table.type")}</TableCell>
            <TableCell className={classes.colDomain}>{i18n.t("compaies.table.domain")}</TableCell>
            <TableCell className={classes.hideMd}>{i18n.t("compaies.table.phone")}</TableCell>
            {showAi && (
              <TableCell align="right">{i18n.t("aiUsage.companyColumn")}</TableCell>
            )}
            <TableCell>{i18n.t("compaies.table.plan")}</TableCell>
            <TableCell align="right" className={classes.hideMd}>{i18n.t("compaies.table.value")}</TableCell>
            <TableCell>{i18n.t("compaies.table.active")}</TableCell>
            <TableCell className={classes.hideMd}>{i18n.t("compaies.table.createdAt")}</TableCell>
            <TableCell>{i18n.t("compaies.table.dueDate")}</TableCell>
            <TableCell className={classes.hideMd}>{i18n.t("compaies.table.lastLogin")}</TableCell>
            <TableCell className={classes.hideMd}>{i18n.t("compaies.table.generateInvoice")}</TableCell>
            <TableCell align="right" className={classes.actionsHead}>{i18n.t("compaies.table.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => {
            const tone = dueTone(row);
            return (
              <TableRow
                key={key}
                className={tone === "warn" ? classes.rowWarn : tone === "down" ? classes.rowDown : undefined}
              >
                <TableCell>
                  <span className={panel.cellStrong}>{row.name || "-"}</span>
                  <span className={classes.sub}>{row.email || "-"}</span>
                </TableCell>
                <TableCell><TenantTypeBadge type={row.type} /></TableCell>
                <TableCell>{renderDomain(row)}</TableCell>
                <TableCell className={classes.hideMd}>{row.phone || "-"}</TableCell>
                {showAi && (
                  <TableCell align="right">{renderAiUsage(row)}</TableCell>
                )}
                <TableCell>{renderPlan(row)}</TableCell>
                <TableCell align="right" className={classes.hideMd}>{i18n.t("compaies.table.money")} {renderPlanValue(row)}</TableCell>
                <TableCell>
                  <StatusBadge
                    size="sm"
                    status={row.status === false ? "inactive" : "active"}
                    label={row.status === false ? i18n.t("compaies.table.no") : i18n.t("compaies.table.yes")}
                  />
                </TableCell>
                <TableCell className={classes.hideMd}>{dateToClient(row.createdAt)}</TableCell>
                <TableCell>
                  <span className={tone === "warn" ? classes.dueWarn : tone === "down" ? classes.dueDown : undefined}>
                    {dateToClient(row.dueDate)}
                  </span>
                  <span className={classes.sub}>{row.recurrence}</span>
                </TableCell>
                <TableCell className={classes.hideMd}>{datetimeToClient(row.lastLogin)}</TableCell>
                <TableCell className={classes.hideMd}>{row.generateInvoice ? i18n.t("compaies.table.yes") : i18n.t("compaies.table.no")}</TableCell>
                <TableCell
                  className={`${classes.actionsCell} ${
                    tone === "warn" ? classes.actionsCellWarn : tone === "down" ? classes.actionsCellDown : ""
                  }`}
                >
                  <span className={panel.rowActions}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => onSelect(row)} aria-label="Editar empresa">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("compaies.table.invoices")}>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/financeiro?companyId=${row.id}`}
                        aria-label={i18n.t("compaies.table.invoices")}
                      >
                        <ReceiptIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("compaies.table.invite")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleInvite(row)}
                          disabled={invitingId === row.id || !row.email}
                          aria-label={i18n.t("compaies.table.invite")}
                        >
                          <MailOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && records.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} className={classes.emptyCell}>
                <EmptyState
                  icon={<BusinessIcon />}
                  title={i18n.t("compaies.table.emptyTitle")}
                  message={i18n.t("compaies.table.emptyMessage")}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={Boolean(invite)} onClose={() => setInvite(null)} maxWidth="sm" fullWidth>
        <ModalTitle
          title={i18n.t("compaies.table.inviteDialogTitle")}
          onClose={() => setInvite(null)}
          closeLabel={i18n.t("compaies.table.inviteClose")}
        />
        <DialogContent>
          {invite && (
            <>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("compaies.table.inviteDialogText")} <strong>{invite.company.name}</strong>
                {invite.company.email ? ` (${invite.company.email})` : ""}.
              </Typography>
              <div className={classes.inviteLinkBox}>
                <span className={classes.inviteLinkText}>{invite.inviteUrl}</span>
                <Tooltip title={i18n.t("compaies.table.inviteCopy")}>
                  <IconButton size="small" onClick={handleCopyInvite} aria-label={i18n.t("compaies.table.inviteCopy")}>
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              {invite.expiresAt && (
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
                  {i18n.t("compaies.table.inviteExpires")} {moment(invite.expiresAt).format("DD/MM/YYYY HH:mm")}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvite(null)}>{i18n.t("compaies.table.inviteClose")}</Button>
          <Button variant="contained" color="primary" startIcon={<FileCopyOutlinedIcon />} onClick={handleCopyInvite}>
            {i18n.t("compaies.table.inviteCopy")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const panel = usePanelStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    paymentMethod: "",
    generateInvoice: true,
    ...TENANT_DEFAULTS,
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      console.log("Lista de empresas carregada:", companyList);
      setRecords(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    console.log("Dados enviados para o backend:", data);
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error(
        "Não foi possível realizar a operação. Verifique se já existe uma empresa com o mesmo nome ou se os campos foram preenchidos corretamente"
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação");
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setFormOpen(false);
    setRecord((prev) => ({
      ...prev,
      id: undefined,
      parentCompanyId: null,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      // campaignsEnabled: false,
      dueDate: "",
      recurrence: "",
      password: "",
      document: "",
      paymentMethod: "",
      generateInvoice: true,
      ...TENANT_DEFAULTS,
    }));
  };

  const handleSelect = (data) => {
    setFormOpen(true);
    // let campaignsEnabled = false;

    // const setting = data.settings.find(
    //   (s) => s.key.indexOf("campaignsEnabled") > -1
    // );
    // if (setting) {
    //   campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    // }

    setRecord((prev) => ({
      ...prev,
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      // campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
      password: "",
      document: data.document || "",
      paymentMethod: data.paymentMethod || "",
      generateInvoice: data.generateInvoice !== undefined ? data.generateInvoice : true,
      type: data.type || TENANT_TYPES.company,
      parentCompanyId: data.parentCompanyId || null,
      slug: data.slug || "",
      customDomain: data.customDomain || "",
      allowCustomBrand: Boolean(data.allowCustomBrand),
      maxCompanies:
        data.maxCompanies === null || data.maxCompanies === undefined
          ? ""
          : data.maxCompanies,
    }));
  };

  const filteredRecords = records.filter(
    (row) =>
      normalizeSearch(`${row.name} ${row.email} ${row.slug} ${row.customDomain}`).includes(
        normalizeSearch(search)
      ) &&
      (statusFilter === "all" || (row.status !== false) === (statusFilter === "active"))
  );

  return (
    <div className={classes.root}>
      <div className={classes.metrics}>
        <StatCard
          label="Empresas cadastradas"
          value={records.length}
          icon={<BusinessIcon />}
          tone="brand"
        />
        <StatCard
          label="Ativas"
          value={records.filter((row) => row.status !== false).length}
          icon={<CheckCircleOutlineIcon />}
          tone="success"
        />
        <StatCard
          label="Inativas"
          value={records.filter((row) => row.status === false).length}
          icon={<BlockIcon />}
          tone="neutral"
        />
      </div>
      <div className={`${panel.toolbar} ${classes.toolbar}`}>
        <TextField
          className={panel.toolbarGrow}
          type="search"
          size="small"
          variant="outlined"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar empresa, e-mail ou domínio…"
          inputProps={{ "aria-label": "Buscar empresa, e-mail ou domínio" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" className={classes.statusFilter}>
          <Select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            inputProps={{ "aria-label": "Filtrar empresas por situação" }}
          >
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="active">Ativas</MenuItem>
            <MenuItem value="inactive">Inativas</MenuItem>
          </Select>
        </FormControl>
        <span className={panel.toolbarSpacer} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            handleCancel();
            setFormOpen(true);
          }}
        >
          Nova empresa
        </Button>
      </div>
      {formOpen && (
        <CompanyForm
          initialValue={record}
          onDelete={handleOpenDeleteDialog}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      )}
      <CompaniesManagerGrid records={filteredRecords} onSelect={handleSelect} loading={loading} />
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
    </div>
  );
}
